<template>
  <v-row no-gutters>
    <v-col cols="12">
      <draggable-lists
        v-bind="$attrs"
        v-on="$listeners"
        child-prop-name="elements"
        :layersDotsItems="layersDotsItems"
        :leftTitle="leftTitle"
        :rightTitle="rightTitle"
        :leftList="layerSchemaAttributes"
        :rightList="searchedItems"
        :is-layers="false"
        @changeGroupName="openInputDialog($event, true, true)"
        @addGroup="openInputDialog($event, true, false)"
        @changeName="openInputDialog($event, false, true)"
        @delete="deleteHandler"
        @setDefaultValue="openDefaultValueDialog"
      >
        <template #listElement="element">
          <draggable-lists-manage-card-item
            v-bind="element"
            v-on="element.listeners"
            :disabled="$attrs.disabled"
            :dots-items="dotsItemGetter(element.elements, element.dotsItems)"
          />
        </template>
        <template #rightListElement="element">
          <draggable-lists-manage-card-item
            v-bind="element"
            :level="1"
            :is-right="true"
            :disabled="$attrs.disabled"
            @changeGroupName="openInputDialog($event, true, true)"
            @addGroup="openInputDialog($event, true, false)"
            @changeName="openInputDialog($event, false, true)"
            @delete="deleteHandler"
            @setDefaultValue="openDefaultValueDialog"
          ></draggable-lists-manage-card-item>
        </template>
        <template #button v-if="hasEditAttributes">
          <base-button
            :disabled="$attrs.disabled"
            translation-path="button.manageAttributes"
            class="mr-0"
            @click="openManageAttributesDialog"
          ></base-button>
        </template>
        <template #search>
          <data-input
            :data-type="{ name: 'text' }"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$i18n.t('default.find')"
            v-model="search"
            clearable
            height="32"
            hide-details
            :disabled="$attrs.disabled"
            class="searchInput"
          >
          </data-input>
        </template>
      </draggable-lists>
    </v-col>
    <main-dialog
      v-if="isDialogLoaded"
      :is-visible.sync="isDialogVisible"
      :title="$i18n.t('dialog.title.deleteGroup')"
      :body="$i18n.t('dialog.body.deleteAttributesGroup', { name: label })"
      agree-text="dialog.agreeDelete"
      @agree="deleteGroup"
      :loading="isConfirmButtonLoading"
    >
    </main-dialog>
    <main-input-dialog
      dialog-width="40%"
      v-if="isInputDialogLoaded"
      :is-visible.sync="isInputDialogVisible"
      :title-translation="inputDialogTitle"
      :agree-text="inputDialogAgree"
      @agree="inputDialogAction"
      :inputs="addGroupInput"
      :loading="isAgreeButtonLoading"
    >
      <template #label.input="slot">
        <v-col>
          <v-row no-gutters>
            <v-col cols="12">
              <data-input v-model="label" v-bind="slot" v-on="$listeners" :rules="slot.rules"></data-input>
            </v-col>
          </v-row>
        </v-col>
      </template>
    </main-input-dialog>
    <main-input-dialog
      dialog-width="40%"
      v-if="isDefaultValueLoaded"
      :is-visible.sync="isDefaultValueVisible"
      :title-translation="$i18n.t('dialog.title.setDefaultValue')"
      agree-text="button.save"
      @agree="saveDefaultValue"
      :inputs="defaultVariableInputs"
    >
      <template #policy_type.input="{ rules }">
        <v-radio-group
          :rules="rules"
          v-model="defaultValuePayload.policy_type"
          @change="onChange"
          class="text-body-2 pa-0"
          style="height: auto;"
        >
          <v-tooltip v-for="item in types" :key="item.value" bottom color="font" :disabled="!item.disabled">
            <template #activator="{ on }">
              <span v-on="on">
                <v-radio :value="item.value" :label="item.text" :disabled="item.disabled"> </v-radio>
              </span>
            </template>
            <span>
              {{ item.disabledText }}
            </span>
          </v-tooltip>
        </v-radio-group>
      </template>
      <template #value.input="slot">
        <v-col cols="6">
          <data-input
            v-bind="slot"
            v-if="defaultValuePayload.policy_type === 'variable'"
            :rules="slot.rules"
            :items="filteredDefaultValuesPolicies"
            item-text="verbose_name"
            item-value="name"
            v-model="defaultValuePayload.value"
            :menu-props="{ offsetY: true }"
            :data-type="selectDataType"
          >
          </data-input>
          <data-input
            v-bind="slot"
            v-else-if="currentEditedAttribute.type === 'dict'"
            :rules="slot.rules"
            :items="currentEditedAttribute.allowed_values"
            :data-type="selectDataType"
            v-model="defaultValuePayload.value"
            :menu-props="{ offsetY: true }"
          >
          </data-input>
          <data-input
            v-model="defaultValuePayload.value"
            v-else
            :data-type="currentEditedAttribute.data_type"
            v-bind="slot"
          >
          </data-input>
        </v-col>
      </template>
    </main-input-dialog>
    <data-source-attributes-editing
      v-if="isAttributesEditingLoaded"
      :is-visible.sync="isAttributesEditingVisible"
      :editing-data-source="layerMetadata"
      :is-data-source-from-layer="true"
    ></data-source-attributes-editing>
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DraggableLists from '@/components/DraggableLists';
import DraggableListsManageCardItem from '@/components/DraggableListsManageCardItem';

export default {
  name: 'DraggableListsManageCard',
  components: {
    DataSourceAttributesEditing: () => import('@/components/DataSourceAttributesEditing'),
    DataInput,
    DraggableListsManageCardItem,
    DraggableLists,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    layerId: {
      type: Number,
      required: true,
    },
    scope: {
      type: String,
      default: 'core',
    },
    hasEditAttributes: {
      type: Boolean,
      default: false,
    },
    leftTitle: {
      type: String,
      default: 'admin.featureCard',
    },
    rightTitle: {
      type: String,
      default: 'admin.availableAttributes',
    },
  },
  data: () => ({
    search: '',
    selectDataType: {
      name: 'select',
    },
    isDefaultValueVisible: false,
    isDefaultValueLoaded: false,
    label: undefined,
    parentLabel: undefined,
    index: undefined,
    layerType: undefined,
    isDialogLoaded: false,
    isDialogVisible: false,
    isInputDialogLoaded: false,
    isInputDialogVisible: false,
    isAgreeButtonLoading: false,
    isConfirmButtonLoading: false,
    isGroup: false,
    isEdit: false,
    editedAttributeName: undefined,
    layersDotsItems: [
      {
        name: 'changeName',
      },
      {
        name: 'setDefaultValue',
      },
    ],
    defaultValuePayload: {
      policy_type: 'fixed',
      value: undefined,
    },
    isAttributesEditingVisible: false,
    isAttributesEditingLoaded: false,
  }),
  computed: {
    attributesGroupNameMaxLength: get('admin/settingsValues@attributes_group_name_max_length'),
    defaultValuesPolicies: get('layers/defaultValuesPolicies'),
    layerMetadata: get('layers/metadata@:layerDataSource'),
    filteredDefaultValuesPolicies() {
      return this.defaultValuesPolicies?.filter(
        policy => policy.data_type.name === this.currentEditedAttribute?.data_type.name
      );
    },
    hasAnyAvailablePolicy() {
      return this.filteredDefaultValuesPolicies?.length > 0;
    },
    currentEditedAttribute() {
      return this.layerMetadata?.attributes_schema.attributes.find(attr => attr.name === this.editedAttributeName);
    },
    types() {
      return [
        {
          text: this.$i18n.t('dialog.body.constant'),
          value: 'fixed',
        },
        {
          text: this.$i18n.t('dialog.body.variable'),
          value: 'variable',
          disabled: this.currentEditedAttribute.type === 'dict' || !this.hasAnyAvailablePolicy,
          disabledText:
            this.currentEditedAttribute.type === 'dict'
              ? this.$i18n.t('dialog.body.defaultValueVariableDict')
              : this.$i18n.t('dialog.body.noDefaultValuesPoliciesForDataType'),
        },
      ];
    },
    currentLayerSchema: sync('layers/currentLayerSchema'),
    layer: get('layers/featuresLayers@:layerId'),
    defaultFormSchema: get('layers/dataSourcesForms@:layerDataSource'),
    layerSchemaAttributes() {
      return this.getAttributes(this.currentLayerSchema, this.layerMetadata.attributes_schema.attributes);
    },
    flattenSchema() {
      if (this.currentLayerSchema) {
        return this.$_flattenStructure(this.currentLayerSchema, 'elements');
      }
      return undefined;
    },
    flattenSchemaNames() {
      if (this.flattenSchema) {
        return this.flattenSchema.map(attribute => attribute.attribute);
      }
      return [];
    },
    searchedItems() {
      if (!this.search) {
        return this.filteredAttributes;
      }
      return this.filteredAttributes.filter(a => a.label.toLowerCase().includes(this.search.toLowerCase()));
    },
    filteredAttributes() {
      return (this.flattenDefaultFormSchema || []).filter(x => !this.flattenSchemaNames.includes(x.attribute));
    },
    flattenDefaultFormSchema() {
      return [...this.$_flattenStructure(this.defaultFormSchema.elements, 'elements')];
    },

    layerDataSource() {
      if (this.layer) {
        return this.layer.data_source_name;
      }
      return undefined;
    },
    inputDialogAction() {
      if (this.isGroup) {
        return this.isEdit ? this.editGroup : this.addGroup;
      }
      return this.changeAttributeName;
    },
    inputDialogTitle() {
      return this.$i18n.t(this.isEdit ? 'dialog.title.changeName' : 'dialog.title.addDraggableGroup');
    },
    inputDialogAgree() {
      return this.isEdit ? 'button.save' : 'dialog.agreeAdd';
    },
    defaultVariableInputs() {
      return [
        {
          name: 'policy_type',
          align: 'baseline',
          clearable: true,
          translationPath: 'dialog.body.type',
          rules: [v => !!v || this.$i18n.t('rules.required')],
          isCustom: true,
        },
        {
          name: 'value',
          translationPath: 'dialog.body.value',
          clearable: true,
          dataType: {
            name: 'text',
          },
        },
      ];
    },
    currentLayerSchemaGroupsNames() {
      return this.currentLayerSchema.map(group => group.label);
    },
    addGroupInput() {
      return [
        {
          name: 'label',
          translationPath: 'dialog.body.name',
          'validate-on-blur': !this.isEdit,
          clearable: true,
          rules: [
            v => !!v || 'rules.required',
            v => !this.currentLayerSchemaGroupsNames.includes(v) || 'rules.groupNameExists',
          ],
          dataType: {
            name: 'text',
            max_length: this.attributesGroupNameMaxLength,
          },
        },
      ];
    },
  },
  watch: {
    isInputDialogVisible(nV) {
      if (!nV) {
        this.resetData();
      }
    },
    isDialogVisible(nV) {
      if (!nV) {
        this.resetData();
      }
    },
  },
  methods: {
    getDefaultValuesPolicies: call('layers/getDefaultValuesPolicies'),
    getDataSourceForm: call('layers/getDataSourceForm'),
    getFeaturesLayer: call('layers/getFeaturesLayer'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    flatten(elements) {
      return elements.reduce((total, current) => {
        if (current.elements) {
          return [...total, ...this.flatten(current.elements)];
        } else {
          return [...total, { ...current }];
        }
      }, []);
    },
    addGroup() {
      this.$store.set('layers/ADD_CURRENT_LAYER_SCHEMA_GROUP!', {
        element_type: 'group',
        elements: [],
        label: this.label,
      });
      this.isInputDialogVisible = false;
    },
    editGroup() {
      this.$store.set('layers/EDIT_CURRENT_LAYER_SCHEMA_GROUP!', {
        index: this.index,
        value: {
          label: this.label,
        },
      });
      this.isInputDialogVisible = false;
    },
    openDefaultValueDialog(e) {
      console.log('~ e', e);
      this.getDefaultValuesPolicies();
      const { attribute, parentLabel, default_value_policy } = e;
      this.editedAttributeName = attribute;
      this.parentLabel = parentLabel;
      if (default_value_policy) {
        this.defaultValuePayload = { ...default_value_policy };
      } else {
        this.defaultValuePayload = {
          policy_type: 'fixed',
          value: undefined,
        };
      }
      this.isDefaultValueVisible = true;
      this.isDefaultValueLoaded = true;
    },
    saveDefaultValue() {
      const { defaultValuePayload } = this;
      this.editAttribute('default_value_policy', { ...defaultValuePayload });
      this.isDefaultValueVisible = false;
    },
    changeAttributeName() {
      this.editAttribute('label', this.label);
      this.isInputDialogVisible = false;
    },
    editAttribute(key, value) {
      const { parentLabel, editedAttributeName } = this;
      this.$store.set('layers/SET_ATTRIBUTE_PROPERTY!', {
        parentLabel,
        editedAttributeName,
        key,
        value,
      });
    },
    deleteGroup() {
      this.$store.set('layers/DELETE_CURRENT_LAYER_SCHEMA_GROUP!', this.index);
      this.isDialogVisible = false;
    },
    onChange() {
      this.defaultValuePayload.value = undefined;
    },

    openManageAttributesDialog() {
      this.isAttributesEditingLoaded = true;
      this.isAttributesEditingVisible = true;
    },
    setData(e) {
      const { index, label, type, parentLabel, attribute } = e;
      this.label = label;
      this.index = index;
      this.editedAttributeName = attribute;
      this.parentLabel = parentLabel;
      // 🤮
      this.layerType = type + 's';
    },
    openInputDialog(e, isGroup, isEdit) {
      this.isGroup = isGroup;
      this.isEdit = isEdit;
      if (isEdit) {
        this.setData(e);
      }
      this.isInputDialogVisible = true;
      this.isInputDialogLoaded = true;
    },
    openConfirmationDialog(e) {
      this.setData(e);
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    deleteHandler(e) {
      this.isGroup = true;
      this.openConfirmationDialog(e);
    },
    resetData() {
      this.isEdit = false;
      this.layerType = undefined;
      this.isGroup = false;
      this.label = undefined;
      this.index = undefined;
      this.isConfirmButtonLoading = false;
      this.isAgreeButtonLoading = false;
    },
    dotsItemGetter(elements, items) {
      const hasAnyRequired = elements && elements.some(element => element.required);
      if (hasAnyRequired) {
        return [
          {
            name: 'changeGroupName',
          },
          {
            name: 'delete',
            disabled: true,
            tooltip: this.$i18n.t('menu.removeDisabledRequiredItems'),
          },
        ];
      }
      return items;
    },
    init() {
      this.$store.set('layers/currentLayer', this.layer);
      const { elements } = this.layer.form_schema;
      this.$store.set('layers/currentLayerSchema', [...elements]);
    },
    async refresh() {
      await Promise.all([
        this.getFeaturesLayer(this.layerId),
        this.getDataSourceForm(this.layerDataSource),
        this.getLayerMetadata({ dataSource: this.layerDataSource }),
      ]);
      this.init();
    },
    getAttributes(elements, metadata) {
      return elements.map(element => {
        if (element.element_type === 'group') {
          return { ...element, elements: this.getAttributes(element.elements, metadata) };
        }
        const { read_only } = metadata.find(el => el.name === element.attribute) || {};
        return {
          ...element,
          readOnly: read_only,
        };
      });
    },
  },
  mounted() {
    this.init();
  },
  created() {
    this.$root.$off('attributesChanged');
    this.$root.$on('attributesChanged', this.refresh);
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
::v-deep .v-list-item__title {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  text-align: left;
}
::v-deep .v-label {
  font-size: 0.875rem !important;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: 0.0178571429em !important;
  font-family: 'Roboto', sans-serif !important;
}
</style>
